/**
 * Host-side remote-control session (the emulator tab). Owns a pairing session:
 * mints a session id + pairing URL for the phone's QR, opens the WebRTC host
 * link over the signaling relay, decodes incoming INPUT frames into a
 * RemoteInputReceiver, and exposes an `afterPoll` hook the gamepad loop calls
 * each frame to fold the freshest remote snapshot into the shared input buffer
 * (merged with the local pad — see inject.ts). Nothing here touches the WASM
 * core or the worker; a phone is just another writer of shared.input.
 *
 * The transport handshake is greenfield/live (needs a relay + a phone), so the
 * end-to-end is the manual check (docs/HANDOFF.md); the marshalling it drives is
 * unit-tested (protocol/touch/inject).
 */

import { decodeInput, pairingUrl } from "./protocol";
import { RemoteInputReceiver } from "./inject";
import {
  createHostLink, WebSocketSignaling, newSessionId, type RemoteLink, type LinkState,
} from "./transport";

export interface RemoteHostSession {
  /** The session id encoded in the pairing URL/QR. */
  readonly sessionId: string;
  /** The URL a phone opens (origin + /remote.html#s=<id>). Render as QR + link. */
  readonly url: string;
  /** Called each frame by the gamepad loop, after the local snapshot is written,
   *  to OR the remote input into `view`. Safe before a link exists (no-op-ish:
   *  applies neutral once a stale window passes). */
  applyInto(view: Int32Array): void;
  /** Subscribe to connection-state changes for the pairing UI. */
  onState(handler: (state: LinkState) => void): void;
  /** Tear down the link + signaling. */
  close(): void;
}

export interface RemoteHostOptions {
  /** Signaling relay URL. Defaults to same-origin `/signal` (keeps COOP/COEP). */
  relayUrl?: string;
  /** App origin used to build the pairing URL. Defaults to location.origin. */
  origin?: string;
  /** Injected clock (Date.now in production) for staleness — testable. */
  now?: () => number;
}

function defaultRelayUrl(): string {
  /* A ?relay= override on the emulator URL lets a dev point at a relay on the
   * LAN during bring-up; otherwise same-origin /signal (COOP/COEP intact). */
  const override =
    typeof location !== "undefined"
      ? new URLSearchParams(location.search).get("relay")
      : null;
  if (override) return override;
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${location.host}/signal`;
}

/**
 * Start a host session. Returns immediately with the pairing URL to display; the
 * WebRTC link opens asynchronously once the phone joins the same signaling
 * session. Frames flow into an internal RemoteInputReceiver; call applyInto()
 * from the input loop to merge them.
 */
export function startRemoteHost(opts: RemoteHostOptions = {}): RemoteHostSession {
  const now = opts.now ?? (() => Date.now());
  const sessionId = newSessionId();
  const origin = opts.origin ?? location.origin;
  const relay = opts.relayUrl ?? defaultRelayUrl();
  /* Embed the relay in the pairing URL so the phone connects to the SAME relay
   * from the QR without the user typing it. */
  const url = pairingUrl(origin, sessionId, relay);
  const receiver = new RemoteInputReceiver();
  const stateHandlers: ((s: LinkState) => void)[] = [];
  let link: RemoteLink | null = null;

  const signaling = new WebSocketSignaling(relay, sessionId, "host");

  void createHostLink(signaling).then((l) => {
    link = l;
    l.onData((ab) => {
      const state = decodeInput(ab);
      if (state) receiver.ingest(state, now());
    });
    l.onState((s) => {
      if (s === "disconnected" || s === "failed") receiver.reset();
      for (const h of stateHandlers) h(s);
    });
  }).catch(() => { /* pairing failed; UI stays "waiting", user can retry */ });

  return {
    sessionId,
    url,
    applyInto(view: Int32Array): void { receiver.apply(view, now()); },
    onState(handler): void { stateHandlers.push(handler); },
    close(): void {
      receiver.reset();
      try { link?.close(); } catch { /* already gone */ }
      try { signaling.close(); } catch { /* already gone */ }
    },
  };
}
